import { CheckIcon, CommentAltIcon, EllipsisIcon, FlagIcon, SendIcon, TrashIcon } from "@xola/icons";
import { Popover } from "@xola/ui-kit";
import { now, PurchaseActionOption } from "common";
import React, { useEffect, useState } from "react";
import { useQuery } from "react-query";
import { useHistory } from "react-router-dom";
import { Experience, Purchase, Timeslot, WaitlistItem } from "types";
import { Menu } from "../../../Common/Menu";
import { SecondaryButton } from "../../../Common/SecondaryButton";
import {
    CollectBalanceOption,
    CollectBalanceOptionsModal,
} from "../../../PurchaseModification/CollectBalanceDeciderModal";
import { CollectPurchaseBalanceViaInvoiceModal } from "../../../PurchaseModification/CollectBalanceViaInvoiceModal";
import { CollectPurchaseBalanceModal } from "../../../PurchaseModification/CollectPurchaseBalanceModal";
import { ConfirmWaitlistModal } from "./ConfirmWaitlistModal";
import { InviteWaitlistModal } from "./InviteWaitlistModal";
import { RemoveWaitlistModal } from "./RemoveWaitlistModal";
import { api } from "@/api/api";
import { MessageModal } from "@/components/Common/MessageModal/MessageModal";
import { updateTimeslotCache } from "@/components/Event/EditEventCapacityModal";
import { useTraveler } from "@/hooks/resources/useTraveler";
import { useSeller } from "@/hooks/useAccount";
import { useAuthorization } from "@/hooks/useAuthorization";
import { useInvalidate } from "@/hooks/useInvalidation";
import { useResource } from "@/hooks/useResource";
import { useModal, useVisibility } from "@/hooks/useVisibility";
import { getEquipmentRosterLineItems } from "@/models/PurchaseItem";
import { getCanConfirm, getCanInvite } from "@/models/WaitlistItem";
import { useCreatePurchaseStore } from "@/stores/createPurchaseStore";

interface WaitlistActionButtonProps {
    timeslot: Timeslot;
    purchase: Purchase;
    item: WaitlistItem;
    popoverParentId?: string;
}

export const WaitlistActionButton = ({ timeslot, purchase, item, popoverParentId }: WaitlistActionButtonProps) => {
    const invalidate = useInvalidate();
    const history = useHistory();
    const { isGranted } = useAuthorization();
    const resetState = useCreatePurchaseStore((store) => store.actions.resetState);

    const [confirmedPurchase, setConfirmedPurchase] = useState<Purchase | null>(null);
    const [currentAction, setCurrentAction] = useState<PurchaseActionOption | null>(null);
    const { traveler: organizer } = useTraveler(purchase.organizer?.id);

    const [isPopoverVisible, { show: showPopover, hide: hidePopover }] = useVisibility(false);

    const messageModalOpen = useModal();
    const removeModal = useModal();
    const inviteModal = useModal();
    const confirmModal = useModal();

    const { data: experience } = useResource<Experience>(["experiences", item.product?.id]);

    // Dashboard timeslots don't carry units; equipment gating needs per-template open counts
    const needsUnits = getEquipmentRosterLineItems(item).length > 0 && !timeslot.units;
    const { data: timeslotWithUnits, refetch: refetchUnits } = useQuery(
        ["waitlist-timeslot-units", timeslot.id],
        async () => {
            const { data } = await api.get<{ data: Timeslot[] } | Timeslot[]>("/timeslots", {
                params: {
                    start: timeslot.arrivalDatetime.format(),
                    product: timeslot.product,
                    include: "units,units.open",
                    ...(timeslot.event?.id && { event: timeslot.event.id }),
                    ...(timeslot.arrivalDatetime.isBefore(now()) && { allowPastSlots: true }),
                },
            });
            const timeslots = Array.isArray(data) ? data : data.data;

            // Without an event filter a product can return sibling slots at the same start, so match by id.
            return timeslots?.find((slot) => slot.id === timeslot.id) ?? timeslots?.[0];
        },
        { enabled: needsUnits },
    );

    // Availability changes elsewhere (modify/refund) patch caches directly, so refresh when the menu opens
    useEffect(() => {
        if (isPopoverVisible && needsUnits) {
            refetchUnits();
        }
    }, [isPopoverVisible, needsUnits, refetchUnits]);

    const gatingTimeslot = timeslotWithUnits?.units ? { ...timeslot, units: timeslotWithUnits.units } : timeslot;

    const canInvite = getCanInvite(useSeller(), gatingTimeslot, item, experience!);
    const canConfirm = getCanConfirm(useSeller(), gatingTimeslot, item, experience!);
    const emailProvided = purchase.customerEmail?.trim()?.length > 0;
    const showMessage = !!purchase?.phone || !!purchase?.customerEmail;

    const invalidatePurchase = async () => {
        const productId = item?.product.id ?? null;
        const arrivalDate = item?.arrivalDate;
        const timeslotParams = {
            timeslot: null,
            event: null,
            productId: productId,
            startDate: arrivalDate,
        };

        const updateParams = {
            isGranted: isGranted,
        };

        invalidate("purchaseItems", item.id);
        invalidate("purchases", purchase.id);

        await updateTimeslotCache(timeslotParams, updateParams);
    };

    const handleCollectBalanceModalClose = () => {
        invalidatePurchase();
        setCurrentAction(null);
    };

    const handleRebookCustomer = () => {
        resetState();
        useCreatePurchaseStore.setState({ organizer: organizer });
        history.push("/cart/init");
    };

    const handleConfirmModalClose = (confirmedPurchase?: Purchase) => {
        confirmModal.hide();

        if (confirmedPurchase) {
            setCurrentAction(PurchaseActionOption.COLLECT_BALANCE_OPTIONS);
            setConfirmedPurchase(confirmedPurchase);
        }
    };

    const handleModalOpen = (callback: () => void) => () => {
        callback();
        hidePopover();
    };

    return (
        <>
            <Popover
                visible={isPopoverVisible}
                placement="bottom-end"
                appendTo={popoverParentId ? document.querySelector(`#${popoverParentId}`) : document.body}
                onClickOutside={hidePopover}
            >
                <SecondaryButton className="ml-4" size="tiny" onClick={showPopover}>
                    <EllipsisIcon />
                </SecondaryButton>

                <Popover.Content>
                    <Menu>
                        <Menu.Item
                            isDisabled={!canConfirm}
                            tooltip={
                                canConfirm
                                    ? null
                                    : "This action cannot be performed as there is no capacity in this event to accommodate this group"
                            }
                            icon={<CheckIcon />}
                            onClick={handleModalOpen(confirmModal.show)}
                        >
                            Confirm
                        </Menu.Item>

                        <Menu.Item
                            isDisabled={!canInvite || !emailProvided}
                            tooltip={
                                canInvite && emailProvided
                                    ? null
                                    : `This action cannot be performed as ${
                                          canInvite
                                              ? "customer's email is not provided"
                                              : "there is no capacity in this event to accommodate this group"
                                      }`
                            }
                            icon={<SendIcon />}
                            onClick={handleModalOpen(inviteModal.show)}
                        >
                            Invite to Book
                        </Menu.Item>

                        {showMessage && (
                            <Menu.Item icon={<CommentAltIcon />} onClick={handleModalOpen(messageModalOpen.show)}>
                                Send Message
                            </Menu.Item>
                        )}

                        <Menu.Item icon={<FlagIcon />} onClick={handleRebookCustomer}>
                            Rebook Customer
                        </Menu.Item>

                        <Menu.Item icon={<TrashIcon />} onClick={handleModalOpen(removeModal.show)}>
                            Remove from Waitlist
                        </Menu.Item>
                    </Menu>
                </Popover.Content>
            </Popover>

            <ConfirmWaitlistModal
                isOpen={confirmModal.isVisible}
                item={item}
                timeslot={timeslot}
                onClose={handleConfirmModalClose}
            />

            <InviteWaitlistModal isOpen={inviteModal.isVisible} item={item} onClose={inviteModal.hide} />

            <RemoveWaitlistModal isOpen={removeModal.isVisible} item={item} onClose={removeModal.hide} />

            <MessageModal
                filters={{ waitlist: purchase.id }}
                to={[{ id: purchase.organizer?.id, type: "traveler" }]}
                isOpen={messageModalOpen.isVisible}
                isPhoneAvailable={!!purchase?.phone}
                onClose={messageModalOpen.hide}
            >
                <div className="text-center text-gray-darker">
                    <span>This message will be sent to the customer {purchase?.customerName}</span>
                </div>
            </MessageModal>

            {confirmedPurchase && (
                <>
                    <CollectPurchaseBalanceModal
                        purchase={confirmedPurchase}
                        isOpen={currentAction === PurchaseActionOption.COLLECT_BALANCE}
                        onClose={handleCollectBalanceModalClose}
                    />

                    <CollectBalanceOptionsModal
                        isOpen={currentAction === PurchaseActionOption.COLLECT_BALANCE_OPTIONS}
                        purchase={confirmedPurchase}
                        onClose={handleCollectBalanceModalClose}
                        onConfirm={(action: CollectBalanceOption) => {
                            if (action === CollectBalanceOption.COLLECT_NOW) {
                                setCurrentAction(PurchaseActionOption.COLLECT_BALANCE);
                            } else if (action === CollectBalanceOption.COLLECT_VIA_INVOICE) {
                                setCurrentAction(PurchaseActionOption.COLLECT_BALANCE_VIA_INVOICE);
                            } else {
                                handleCollectBalanceModalClose();
                            }
                        }}
                    />

                    <CollectPurchaseBalanceViaInvoiceModal
                        purchase={confirmedPurchase}
                        item={confirmedPurchase.items[0]}
                        isOpen={currentAction === PurchaseActionOption.COLLECT_BALANCE_VIA_INVOICE}
                        onClose={handleCollectBalanceModalClose}
                    />
                </>
            )}
        </>
    );
};
